import React, { useMemo, useState } from 'react'
import { ChevronDown, PanelBottom, PanelRight, Play, X } from 'lucide-react'
import { mergeClasses } from '@/helpers/styling'
import { assertNever } from '@/utils/assert-never'
import { isMacOS } from '@/utils/operating-system'
import { Button, MiniButton, Select } from '@/components/ui-primitives/small'
import { SimpleTooltip } from '@/components/ui-primitives/simple-tooltip'
import { CodeEditor } from '@/components/editor/code-editor'
import { EditorSize, SizeSwitch } from '@/components/editor/size-switch'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui-primitives/resizable'
import { SparqlEditor } from '@/components/SPARQLEditor'
import { FloatingStateContext } from '@/components/editor/panels/panel-registry'
import { useLocalSettings } from '@/components/editor/local-settings'
import { resolvePlaceholders, WidgetQueryStatus } from '@/components/editor/widget-editor/query-status'
import { QueryResults } from '@/components/editor/widget-editor/query-results'
import { editPendingQuery, takePendingQueries, usePendingQueries } from '@/components/editor/widget-editor/pending-queries'
import { AnyConfigDesc, LabelWithHelp } from '@/components/editor/widget-editor/form-fields'
import { QUERY_EDITOR_ID } from '@/components/editor/widget-editor/editor-tabs'

/** Where the query card is shown: on the widget, in the side panel, or in a floating window */
export type QuerySize = EditorSize

/** A query is edited on its widget or in a floating window; in the settings it is just their Query tab */
export const QUERY_SIZES: EditorSize[] = [ "overlay", "floating" ]

export const QUERY_STATUS_STYLE = {
  success: "bg-[#e7f5ee] text-[#1f6b45]",
  pending: "bg-muted text-muted-foreground",
  error: "bg-[#fde8e8] text-[#9b1c1c]",
  disabled: "bg-[#fff4dc] text-[#6b4600]",
} as const

type DetailsTab = "results" | "placeholders"

/**
 * A widget's query with its run status, results and placeholders, the same in every size.
 * Edits stay pending until Run, which hands them to `apply`.
 */
export function QueryCard(props: {
  size: QuerySize
  path: string
  status: WidgetQueryStatus | null
  /** The widget's SPARQL fields, usually one */
  fields: ReadonlyArray<AnyConfigDesc>
  /** Left out when the frame around the card shows one, as the floating window does */
  title?: string
  /** The values the widget runs with now */
  applied: Record<string, unknown>
  apply: (edits: Record<string, string>) => void
  onResize: (size: QuerySize) => void
  onClose?: () => void
  /** As the widget settings' Query tab, which has the field's label and help, and takes the focus requests */
  inSettings?: boolean
}) {
  const { size, path, status } = props
  const pending = usePendingQueries(path)
  const hasPending = Object.keys(pending).length > 0
  // local to this card: the settings' Query tab and the query's own card can be open side by side
  const [ details, setDetails ] = useState<{ open: boolean, tab: DetailsTab }>({ open: false, tab: "results" })
  // floating, the results can go beside the query instead of below it; below keeps the layout of the other sizes
  const [ beside, setBeside ] = useState(false)
  const useYasqe = useLocalSettings(s => s.sparqlEditor === "yasqe") && size !== "docked"
  const isBeside = size === "floating" && beside
  const detailsShown = isBeside || details.open

  const placeholders = useMemo(
    () => status?.template ? resolvePlaceholders(status.template, status.published) : [],
    [ status?.template, status?.published ]
  )

  const run = () => {
    setDetails({ open: true, tab: "results" })
    const edits = takePendingQueries(path)
    if (Object.keys(edits).length) props.apply(edits)
    else status?.rerun()
  }

  const detailsBar = (
    <DetailsBar size={size}
                open={detailsShown}
                tab={details.tab}
                onSelect={tab => setDetails({ open: true, tab })}
                onToggle={() => setDetails(d => ({ ...d, open: !d.open }))}
                beside={isBeside}
                onToggleBeside={() => setBeside(b => !b)}
                resultCount={status?.status === "success" ? status.rowCount : null}
                placeholderCount={placeholders.length} />
  )

  const detailsContent = detailsShown && (
    <div className={mergeClasses("flex min-h-0 flex-col overflow-auto", {
      "flex-1": isBeside,
      "max-h-[45vh]": size === "docked",
    })}>
      {details.tab === "results" ? (
        <QueryResults status={status} bare />
      ) : (
        <Placeholders placeholders={placeholders} />
      )}
    </div>
  )

  const editors = (
    <div id={props.inSettings ? QUERY_EDITOR_ID : undefined}
         className={mergeClasses("flex min-h-0 flex-col gap-2", { "flex-1 p-2": size !== "docked" })}>
      {props.fields.map(field => {
        const value = pending[field.name] ?? (props.applied[field.name] as string | undefined) ?? ''
        const edit = (next: string) => editPendingQuery(path, field.name, next, props.applied[field.name])
        return (
          <div key={field.name} className={mergeClasses("flex min-h-0 flex-col gap-1", { "flex-1": size !== "docked" })}>
            {(props.inSettings || props.fields.length > 1) && (
              <LabelWithHelp label={field.label} helpComponent={field.helpComponent} />
            )}
            {useYasqe ? (
              // the floating state makes YASQE fill the card instead of sizing to its content
              <FloatingStateContext.Provider value="floating-active">
                <div className={mergeClasses("flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border-[1px] border-border",
                                             // one frame and a flat gutter, like the ACE editor
                                             "[&_.CodeMirror]:!border-0 [&_.CodeMirror-gutters]:!border-r-0 [&_.CodeMirror-gutters]:!bg-[#f0f0f0]")}>
                  <SparqlEditor id={`query-card-${size}-${field.name}`}
                                mode="update-value"
                                hideButtons
                                value={value}
                                onBlur={edit} />
                </div>
              </FloatingStateContext.Provider>
            ) : (
              <CodeEditor mode="sparql"
                          name={`${field.name}-${size}`}
                          autoFocus={size !== "docked"}
                          fullHeight={size !== "docked"}
                          minHeight={size === "docked" ? (details.open ? "30vh" : "55vh") : undefined}
                          onRun={run}
                          value={value}
                          onValueChange={edit} />
            )}
          </div>
        )
      })}
    </div>
  )

  return (
    <div className={mergeClasses("flex min-h-0 flex-col bg-white0 text-[13px]", { "h-full": size !== "docked" })}>
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {props.title && <span className="shrink-0 font-exo font-semibold">{props.title}</span>}
          {status && (
            <SimpleTooltip tooltip={detailsShown && details.tab === "results" ? "Hide results" : "Show results"}>
              <button type="button"
                      onClick={() => setDetails(d => ({
                        open: isBeside || !(d.open && d.tab === "results"),
                        tab: "results",
                      }))}
                      className={mergeClasses("min-w-0 truncate whitespace-nowrap rounded-full px-2 py-0.5 text-xs", QUERY_STATUS_STYLE[status.status])}>
                {describeQueryStatus(status)}
              </button>
            </SimpleTooltip>
          )}
          {hasPending && <span className="min-w-0 truncate whitespace-nowrap text-xs text-[#6b4600]">Edited · not applied yet</span>}
        </div>
        <Button variant="purple"
                className="shrink-0"
                title={`${hasPending ? "Apply your edits and run the query" : "Re-run the query"} (${isMacOS ? "⌘" : "Ctrl+"}↵ in the editor)`}
                disabled={!hasPending && !status}
                onClick={run}>
          <Play size={12} />
          Run
        </Button>
        {/* in the settings and the floating window, the frame around the card has the switch */}
        {size === "overlay" && (
          <SizeSwitch value={size}
                      onChange={props.onResize}
                      sizes={QUERY_SIZES} />
        )}
        {props.onClose && (
          <MiniButton title="Close [Esc]" aria-label="Close" onClick={props.onClose}>
            <X size={14} />
          </MiniButton>
        )}
      </div>

      {isBeside ? (
        // editor and details side by side, with the same split handle as the event log and triggers
        <ResizablePanelGroup orientation="horizontal" className="min-h-0 flex-1">
          <ResizablePanel defaultSize="58%" minSize="25%" className="flex min-h-0 flex-col">
            {editors}
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel minSize="20%" className="flex min-h-0 flex-col">
            {detailsBar}
            {detailsContent}
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className={mergeClasses("flex min-h-0 min-w-0 flex-col", {
            "min-h-24 flex-1": size !== "docked",
            "px-3 py-2": size === "docked",
          })}>
            {editors}
          </div>
          {/* below the query, the details take what their content needs (up to 60%) and the editor the rest */}
          <div className={mergeClasses("flex min-h-0 min-w-0 flex-col", {
            "max-h-[60%]": size !== "docked",
            "shrink-0": size === "docked",
          })}>
            {detailsBar}
            {detailsContent}
          </div>
        </div>
      )}
    </div>
  )
}

function DetailsBar(props: {
  size: QuerySize
  open: boolean
  tab: DetailsTab
  onSelect: (tab: DetailsTab) => void
  onToggle: () => void
  beside: boolean
  onToggleBeside: () => void
  resultCount: number | null
  placeholderCount: number
}) {
  const { tab } = props
  const tabs = [
    { id: "results" as const, label: "Results", count: props.resultCount },
    { id: "placeholders" as const, label: "Placeholders", count: props.placeholderCount },
  ]
  return (
    <div className={mergeClasses("flex items-center gap-0.5 border-border bg-muted/40 px-2", {
      "border-t": !props.beside,
      "border-b": props.beside || props.open,
    })}>
      <div role="tablist" aria-label="Query details" className="flex gap-0.5">
        {tabs.map(({ id, label, count }) => {
          const selected = props.open && tab === id
          return (
            <button key={id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => props.onSelect(id)}
                    className={mergeClasses("-mb-px border-b-2 border-transparent px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground", {
                      "border-[#51479e] font-semibold text-[#51479e] hover:text-[#51479e]": selected,
                    })}>
              {label}
              {count != null && <span className="ml-1 font-normal text-muted-foreground">{count}</span>}
            </button>
          )
        })}
      </div>
      <div className="flex-grow" />
      {props.size === "overlay" || props.size === "floating" ? <EditorChoice /> : null}
      {props.size === "floating" && (
        <MiniButton title={props.beside ? "Results below the query" : "Results beside the query"}
                    aria-pressed={props.beside}
                    onClick={props.onToggleBeside}>
          {props.beside ? <PanelBottom size={13} /> : <PanelRight size={13} />}
        </MiniButton>
      )}
      {!props.beside && (
        <MiniButton title={props.open ? "Hide details" : "Show details"} onClick={props.onToggle}>
          <ChevronDown size={13} className={mergeClasses("transition-transform", { "rotate-180": !props.open })} />
        </MiniButton>
      )}
    </div>
  )
}

/** YASQE adds autocompletion and formatting; the side panel keeps ACE, which fits its width */
function EditorChoice() {
  const sparqlEditor = useLocalSettings(s => s.sparqlEditor)
  return (
    // mb-0: Bootstrap gives labels a bottom margin
    <label className="mb-0 mr-1 flex items-center gap-1.5 text-xs text-muted-foreground">
      Editor
      <Select value={sparqlEditor}
              onChange={e => useLocalSettings.setState({ sparqlEditor: e.target.value as typeof sparqlEditor })}
              className="h-6 w-auto rounded-md !px-1.5 text-xs text-foreground">
        <option value="ace">ACE</option>
        <option value="yasqe">YASQE</option>
      </Select>
    </label>
  )
}

function Placeholders(props: { placeholders: { placeholder: string, value: unknown }[] }) {
  if (!props.placeholders.length) {
    return <div className="p-3 text-xs text-muted-foreground">This query has no placeholders.</div>
  }
  return (
    <div className="flex flex-col gap-1 p-3 text-xs">
      <span className="font-semibold">Resolved right now</span>
      {props.placeholders.map(({ placeholder, value }) => (
        <div key={placeholder} className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 font-mono text-[#51479e]">{`{{${placeholder}}}`}</span>
          <span className="text-muted-foreground">=</span>
          {value == null ? (
            <span className="text-[#6b4600]">no value yet</span>
          ) : String(value).trim() === "" ? (
            <span className="text-muted-foreground">(empty)</span>
          ) : (
            <span className="truncate font-mono" title={String(value)}>{String(value)}</span>
          )}
        </div>
      ))}
    </div>
  )
}

export function describeQueryStatus(status: WidgetQueryStatus) {
  switch (status.status) {
    case "success":
      return [
        `${status.rowCount ?? 0} rows`,
        status.durationMs != null && `${status.durationMs} ms`,
      ].filter(Boolean).join(" · ")
    case "pending":
      return "Running…"
    case "error":
      return `Error: ${status.error}`
    case "disabled":
      return "Not running: waiting for placeholder values"
    default:
      return assertNever(status.status)
  }
}
